// The one place the app talks to the network: same-origin calls to the Worker (/api/…), which forwards game calls with the
// session's token. Game errors (HTTP 200 + { Error, prompts }) become GamePromptError; a 401 means the session is gone.
import type { GameErrorBody, Prompt, WithPrompts } from "./types";

export class GamePromptError extends Error {
  prompts: Prompt[];
  code: string;
  constructor(message: string, prompts: Prompt[] = [], code = "game") {
    super(message);
    this.name = "GamePromptError";
    this.prompts = prompts.length ? prompts : [{ prompt_type: "prompt_toast", text: message }];
    this.code = code;
  }
}

type Listener<T> = (v: T) => void;
const promptListeners = new Set<Listener<Prompt[]>>();
const signOutListeners = new Set<Listener<void>>();

/** Flash messages the game attaches to successful answers, and errors shown as prompts. */
export const onPrompts = (fn: Listener<Prompt[]>) => (promptListeners.add(fn), () => void promptListeners.delete(fn));
export const emitPrompts = (prompts: Prompt[]) => promptListeners.forEach((fn) => fn(prompts));
/** The Worker said the session is gone (401). */
export const onSignedOut = (fn: Listener<void>) => (signOutListeners.add(fn), () => void signOutListeners.delete(fn));
const emitSignedOut = () => signOutListeners.forEach((fn) => fn());
/** Another same-origin call (the map blob) got a 401. */
export const signedOutByServer = emitSignedOut;

const isErrorBody = (d: unknown): d is GameErrorBody => !!d && typeof d === "object" && typeof (d as GameErrorBody).Error === "string";

async function post<T>(url: string, body: Record<string, unknown>): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
    });
  } catch {
    throw new GamePromptError("No connection. Check your network and try again.", [], "net");
  }
  if (res.status === 401) {
    emitSignedOut();
    throw new GamePromptError("You're signed out.", [], "signed_out");
  }
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new GamePromptError("The game's answer couldn't be read.", [], "parse");
  }
  if (isErrorBody(data)) {
    if (data.Error === "signed_out") {
      emitSignedOut();
      throw new GamePromptError("You're signed out.", [], "signed_out");
    }
    throw new GamePromptError(data.Error, data.prompts ?? [], res.ok ? "game" : "http");
  }
  if (!res.ok) throw new GamePromptError(`Request failed (${res.status}).`, [], "http");
  const d = data as WithPrompts;
  if (Array.isArray(d.prompts) && d.prompts.length) emitPrompts(d.prompts);
  return data as T;
}

/** POST /api/game/<endpoint> — the game's API through the Worker. `wid`/`vid` go in the body like any other field. */
export const gameCall = <T>(endpoint: string, body: Record<string, unknown> = {}): Promise<T> => post<T>(`/api/game/${endpoint}`, body);

/** POST to one of the Worker's own routes (/api/tags/<wid>), with the same error and sign-out handling as game calls. */
export const workerPost = <T>(path: string, body: Record<string, unknown>): Promise<T> => post<T>(`/api/${path}`, body);

/** GET one of the Worker's own routes that needs the session: a 401 signs out like a game call does. */
export async function workerGet<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/${path}`, { credentials: "same-origin", headers: { accept: "application/json" } });
  } catch {
    throw new GamePromptError("No connection. Check your network and try again.", [], "net");
  }
  if (res.status === 401) {
    emitSignedOut();
    throw new GamePromptError("You're signed out.", [], "signed_out");
  }
  const data = (await res.json().catch(() => null)) as unknown;
  if (isErrorBody(data)) throw new GamePromptError(data.Error, data.prompts ?? [], "http");
  if (!res.ok || !data) throw new GamePromptError(`Request failed (${res.status}).`, [], "http");
  return data as T;
}

/** POST /api/session/<action> */
export const sessionCall = <T>(action: string, body: Record<string, unknown> = {}): Promise<T> => post<T>(`/api/session/${action}`, body);

export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin", headers: { accept: "application/json" } });
  if (!res.ok) throw new GamePromptError(`Request failed (${res.status}).`, [], "http");
  return (await res.json()) as T;
}
